import React, { useCallback, useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Grid,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Alert,
    Tooltip,
} from "@mui/material";
import {
    Close,
    OpenInNew,
    Person,
    SendSharp,
    Email as EmailIcon,
    AccountBalanceWallet,
    AddCircleOutline,
    CheckCircle,
    ErrorOutline,
} from "@mui/icons-material";
import { MdWhatsapp } from "react-icons/md";
import {
    getUser,
    getUserServiceStatus,
    getUserSubscriptions,
    getEmailConfig,
    getWbConfig,
} from "../../api/superAdminApi";
import { listEmailDomains, isDomainVerified } from "../../api/emailBusinessApi";
import { USER_SERVICES, openUserServiceInNewTab } from "./adminServiceConfig";
import { formatRoleLabel, parseUserRoles } from "../UserManagement/roleLabels";
import { formatSubscriptionDate } from "./adminUtils";
import { WB_SX } from "../../ui/ux/uiTokens";

const SERVICE_ICONS = {
    profile: <Person sx={{ fontSize: 28 }} />,
    personal_whatsapp: <SendSharp sx={{ fontSize: 28 }} />,
    email: <EmailIcon sx={{ fontSize: 28 }} />,
    whatsapp_business: <MdWhatsapp size={28} />,
    credit_billing: <AccountBalanceWallet sx={{ fontSize: 28 }} />,
};

const ServiceCard = ({ service, status, onOpen }) => {
    const configured = service.isAccount || status?.configured;
    const healthy = status?.healthy;
    const subscription = status?.subscription;

    return (
        <Grid item xs={12} sm={6}>
            <Card
                elevation={0}
                sx={{
                    ...WB_SX.card,
                    height: "100%",
                    borderLeft: `4px solid ${service.accent}`,
                    transition: "box-shadow 0.15s ease, transform 0.15s ease",
                    "&:hover": { boxShadow: "0 8px 24px rgba(15,23,42,0.1)", transform: "translateY(-2px)" },
                }}
            >
                <CardActionArea onClick={() => onOpen(service.slug)} sx={{ height: "100%", alignItems: "stretch" }}>
                    <CardContent sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: 2,
                                    bgcolor: `${service.accent}14`,
                                    color: service.accent,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {SERVICE_ICONS[service.id]}
                            </Box>
                            <Tooltip title="Opens in new tab">
                                <OpenInNew sx={{ fontSize: 18, color: "text.secondary" }} />
                            </Tooltip>
                        </Box>

                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {service.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.55, flex: 1 }}>
                            {service.description}
                        </Typography>

                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {service.isAccount ? (
                                <Chip size="small" icon={<CheckCircle />} label="Always available" sx={{ fontWeight: 600 }} />
                            ) : configured ? (
                                <Chip size="small" icon={<CheckCircle />} label="Configured" color="success" variant="outlined" sx={{ fontWeight: 600 }} />
                            ) : (
                                <Chip
                                    size="small"
                                    icon={<AddCircleOutline />}
                                    label="Set up service"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                            {!service.isAccount && configured && healthy != null && (
                                <Chip
                                    size="small"
                                    icon={healthy ? <CheckCircle /> : <ErrorOutline />}
                                    label={healthy ? "Healthy" : "Unhealthy"}
                                    color={healthy ? "success" : "error"}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                            {subscription && (
                                <Chip
                                    size="small"
                                    label={
                                        subscription.is_expired
                                            ? "Sub expired"
                                            : `Sub: ${subscription.days_remaining ?? 0}d`
                                    }
                                    color={subscription.is_expired ? "error" : "default"}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                            {status?.domainVerification && service.id === "email" && (
                                <Chip
                                    size="small"
                                    label={
                                        status.domainVerification === "SUCCESS"
                                            ? "Domain verified"
                                            : "Domain pending"
                                    }
                                    color={status.domainVerification === "SUCCESS" ? "success" : "warning"}
                                    variant="outlined"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                        </Box>

                        {subscription?.subscription_end && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                Ends {formatSubscriptionDate(subscription.subscription_end)}
                            </Typography>
                        )}
                    </CardContent>
                </CardActionArea>
            </Card>
        </Grid>
    );
};

const UserServicesModal = ({ open, user, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [detail, setDetail] = useState(null);
    const [serviceMeta, setServiceMeta] = useState({});

    const load = useCallback(async (userId) => {
        if (!userId) return;
        setLoading(true);
        setError("");
        try {
            const [userData, svcStatus, subs, emailCfg, wbCfg, domainRows] = await Promise.all([
                getUser(userId),
                getUserServiceStatus(userId).catch(() => null),
                getUserSubscriptions(userId).catch(() => null),
                getEmailConfig(userId),
                getWbConfig(userId),
                listEmailDomains({ user_id: userId, per_page: 100 }).catch(() => []),
            ]);

            setDetail(userData);
            const configs = userData.configs || {};

            setServiceMeta({
                profile: { configured: true },
                personal_whatsapp: {
                    configured: configs.personal_whatsapp ?? !!svcStatus?.personal_whatsapp,
                    healthy: svcStatus?.personal_whatsapp?.is_healthy,
                    subscription: subs?.personal_whatsapp,
                },
                email: {
                    configured: configs.email ?? !!emailCfg,
                    healthy: svcStatus?.email?.is_healthy,
                    subscription: subs?.email,
                    domainVerification: domainRows.some(isDomainVerified)
                        ? "SUCCESS"
                        : domainRows.length
                          ? "PENDING"
                          : null,
                },
                whatsapp_business: {
                    configured: configs.whatsapp_business ?? !!wbCfg,
                    healthy: svcStatus?.whatsapp_business?.is_healthy,
                    subscription: subs?.whatsapp_business,
                },
            });
        } catch (err) {
            setError(err.message || "Failed to load user services");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open && user?.id) load(user.id);
    }, [open, user?.id, load]);

    const handleOpenService = (slug) => {
        if (!user?.id) return;
        openUserServiceInNewTab(user.id, slug);
    };

    if (!user) return null;

    const activeUser = detail || user;
    const roleChips = parseUserRoles(activeUser.user_role);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            User #{activeUser.id}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {activeUser.name || "Unnamed user"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {activeUser.email}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                            {activeUser.account_disabled && (
                                <Chip label="Disabled" size="small" color="error" />
                            )}
                            {roleChips.map((r) => (
                                <Chip key={r} label={formatRoleLabel(r)} size="small" variant="outlined" />
                            ))}
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small" aria-label="Close">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.65 }}>
                    Select a service to manage. Each opens in a <strong>new browser tab</strong> so you can keep the user list open.
                </Typography>

                {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : (
                    <Grid container spacing={2}>
                        {USER_SERVICES.map((service) => (
                            <ServiceCard
                                key={service.id}
                                service={service}
                                status={serviceMeta[service.id]}
                                onOpen={handleOpenService}
                            />
                        ))}
                    </Grid>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default UserServicesModal;
